import { useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  Badge,
  Button,
  Drawer,
  Empty,
  Modal,
  Popover,
  Select,
  Slider,
  Spin,
  Table,
  Tooltip,
  message,
} from "antd";
import {
  InfoCircleOutlined,
  PlusOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import type { ColumnsType as AntColumnsType } from "antd/es/table";

import { useTrialPlans } from "@/hooks/useTrialPlans";
import { useInvestigatorDirectory } from "@/hooks/usePlannedSites";
import { useSiteTrialMetrics } from "@/hooks/useSiteTrialMetrics";
import { ColumnButton } from "@/components/UI/TableToolbar/ColumnsMenu/ColumnButton";
import { IconButton } from "@/components/UI/TableToolbar/IconButton";
import FilterIcon from "@/assets/icons/filter.svg?react";
import ClientDownloadButton from "@/components/UI/ClientDownloadButton/ClientDownloadButton";
import { ColumnVisibility, type ColumnsType } from "@/types/tableState.types";
import InvestigatorProfileDrawer, {
  type InvestigatorProfile,
} from "./components/InvestigatorProfileDrawer";
import { plannedSiteFromInvestigator } from "./plannedSites";
import type { PlannedSite } from "./types";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import {
  DEFAULT_WEIGHTS,
  PILLARS,
  WEIGHT_PRESETS,
  bestPairPerSite,
  buildPairs,
  groupScores,
  normalizeWeights,
  scorePairs,
  type PillarDef,
  type ScoredPair,
  type ShortlistWeights,
} from "./shortlist";

const TABLE_Y = "calc(100vh - 330px)";

/** Common indications per TA, until the backend serves mastered lists. */
const INDICATIONS_BY_TA: Record<string, string[]> = {
  Oncology: [
    "NSCLC",
    "SCLC",
    "Breast Cancer",
    "Colorectal Cancer",
    "Prostate Cancer",
    "Melanoma",
    "DLBCL",
    "Multiple Myeloma",
  ],
  Neurology: [
    "Alzheimer's Disease",
    "Parkinson's Disease",
    "Multiple Sclerosis",
    "Epilepsy",
  ],
  Immunology: [
    "Rheumatoid Arthritis",
    "Psoriasis",
    "Ulcerative Colitis",
    "Crohn's Disease",
  ],
  Cardiology: ["Heart Failure", "Atrial Fibrillation", "Hypertension"],
};

const LINE_OPTIONS = [
  { value: "", label: "Any line" },
  { value: "1L", label: "1L (first line)" },
  { value: "2L+", label: "2L+ (later lines)" },
];
const SIZE_OPTIONS = [10, 25, 50, 100, 250];
const ALL_PHASES = ["Phase 1", "Phase 1/2", "Phase 2", "Phase 2/3", "Phase 3"];

const DEFAULT_COLUMNS: ColumnsType[] = [
  {
    fieldName: "investigator",
    visibility: ColumnVisibility.VISIBLE,
    mandatory: true,
  },
  { fieldName: "location", visibility: ColumnVisibility.VISIBLE },
  { fieldName: "experience", visibility: ColumnVisibility.VISIBLE },
  { fieldName: "site_score", visibility: ColumnVisibility.HIDDEN },
  { fieldName: "investigator_score", visibility: ColumnVisibility.HIDDEN },
  ...PILLARS.map((p) => ({
    fieldName: `pillar_${p.key}`,
    visibility: ColumnVisibility.HIDDEN,
  })),
  { fieldName: "score", visibility: ColumnVisibility.VISIBLE, mandatory: true },
];

/** Labeled filter control: small uppercase title with an explanatory tooltip. */
function FilterField({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <Tooltip title={tooltip}>
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[#8A97A3] cursor-default w-fit">
          {label}
        </span>
      </Tooltip>
      {children}
    </div>
  );
}

/** Score hover card: the weighted pillar mix behind the number. */
function ScoreBreakdown({
  pair,
  weights,
  defs,
}: {
  pair: ScoredPair;
  weights: ShortlistWeights;
  defs: PillarDef[];
}) {
  const w = normalizeWeights(weights, defs);
  return (
    <div className="w-[300px]">
      <p className="text-[12.5px] font-semibold text-[#1F2933] mb-2 mt-0">
        {pair.score.toFixed(1)} = weighted pillar mix
      </p>
      {defs.map((p) => (
        <div
          key={p.key}
          className="grid grid-cols-[118px_1fr_82px] items-center gap-2 mb-1 text-[11px]"
        >
          <span className="text-[#374151]">{p.label}</span>
          <span className="h-[6px] rounded-[3px] bg-[#EFF1F2] overflow-hidden">
            <span
              className={`block h-full ${p.group === "site" ? "bg-[#0C98D0]" : "bg-[#6D5FC8]"}`}
              style={{
                width: `${Math.round((pair.pillars[p.key] ?? 0.5) * 100)}%`,
              }}
            />
          </span>
          <span className="text-[#8A97A3] tabular-nums text-right">
            {Math.round(w[p.key] * 100)}% ×{" "}
            {Math.round(pair.pillars[p.key] * 100)}
          </span>
        </div>
      ))}
      <p className="text-[11px] text-[#8A97A3] mt-2 mb-0 pt-2 border-t border-[#F0F2F4]">
        Experience: {pair.n_profile_trials} trial
        {pair.n_profile_trials === 1 ? "" : "s"} in profile, {pair.n_evidence}{" "}
        with enrollment data — thin history is shrunk toward the median.
      </p>
    </div>
  );
}

function MethodModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      title="How scoring works"
      open={open}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          type="primary"
          className="bg-[#0C98D0] border-none"
          onClick={onClose}
        >
          Close
        </Button>,
      ]}
      width={620}
    >
      <p className="text-[12.5px] text-[#6B7280] mt-1">
        The same methodology as the NSCLC Top-250 site recommendation
        deliverable, parameterized to your trial profile.
      </p>
      <ol className="text-[13px] text-[#374151] leading-relaxed pl-5">
        <li className="mb-2">
          <b>Comparable units.</b> Every raw signal (patients enrolled, delivery
          vs target, phase experience…) becomes a percentile within the cohort
          being ranked, so metrics on different scales can combine.
        </li>
        <li className="mb-2">
          <b>Relevance.</b> Trial history is weighted by how well each trial
          matches your target profile — therapeutic area, phase.
        </li>
        <li className="mb-2">
          <b>Evidence shrinkage.</b> Percentiles are pulled toward the median
          when a pair has little history (empirical-Bayes). A one-trial fluke
          cannot outrank a consistent performer.
        </li>
        <li className="mb-2">
          <b>Weighted pillars.</b> Pillar scores combine into one 0–100
          composite using the weights you configure. Defaults come from a
          reliability analysis of which signals repeat across trials.
        </li>
      </ol>
      <p className="text-[13px] text-[#374151] leading-relaxed">
        <b>Experience &amp; evidence.</b> The Experience column counts the
        trials each (site, investigator) pair has supported that match your
        current profile, alongside their total. Pairs with no enrollment
        evidence at all are not ranked; the number excluded is shown above the
        list. Experience is about how much history backs the score, not how good
        the score is.
      </p>
    </Modal>
  );
}

export default function TrialPlanningShortlist() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;

  const { investigators, isLoading } = useInvestigatorDirectory();

  // null = untouched: the plan's own indication/phase prefill the profile and
  // a user selection overrides them, with no state syncing needed.
  const [taOverride, setTaOverride] = useState<string | null>(null);
  const [indicationOverride, setIndicationOverride] = useState<string | null>(
    null,
  );
  const [phaseOverride, setPhaseOverride] = useState<string | null>(null);
  const [line, setLine] = useState<string>("");
  const [regions, setRegions] = useState<string[]>([]);
  const [n, setN] = useState(50);
  const [grouped, setGrouped] = useState(false);
  const simplified = useFeatureFlag(FEATURE_FLAGS.PLANNING_SIMPLIFIED, true);
  const [weights, setWeights] = useState<ShortlistWeights>({
    ...DEFAULT_WEIGHTS,
  });
  const [preset, setPreset] = useState<string>("Default");
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnsType[]>(DEFAULT_COLUMNS);
  const [lockedColumns, setLockedColumns] = useState(1);
  const [activeInv, setActiveInv] = useState<InvestigatorProfile | null>(null);

  const allTas = useMemo(
    () =>
      [
        ...new Set(investigators.flatMap((i) => i.therapeutic_areas ?? [])),
      ].sort(),
    [investigators],
  );
  const allRegions = useMemo(
    () =>
      [
        ...new Set(investigators.map((i) => i.region ?? "").filter(Boolean)),
      ].sort(),
    [investigators],
  );

  const planTa = plan?.indication ?? "";
  const ta = taOverride ?? (allTas.includes(planTa) ? planTa : "");
  const phase = phaseOverride ?? plan?.phase ?? "";
  // Indication options within the selected TA; the plan's own indication is
  // always offered. Mastered indication lists arrive with the backend endpoint.
  const indicationOptions = useMemo(() => {
    const base = INDICATIONS_BY_TA[ta] ?? [];
    const planIndication = plan?.indication ?? "";
    return planIndication && !base.includes(planIndication) && ta
      ? [planIndication, ...base]
      : base;
  }, [ta, plan?.indication]);
  const indication =
    indicationOverride ??
    (ta && indicationOptions.includes(plan?.indication ?? "")
      ? (plan?.indication ?? "")
      : "");

  // Site performance metrics for the whole cohort. The method scores site
  // pillars from these; without them only investigator pillars are available.
  const { rows: siteTrialRows, isLoading: metricsLoading } =
    useSiteTrialMetrics();
  const pillarDefs: PillarDef[] = PILLARS;
  const activeWeights = weights;

  // Profile changes rebuild pairs (relevance, percentiles, shrinkage); weight
  // changes only re-run the cheap composite below — sliders stay instant.
  const { pairs, excluded } = useMemo(
    () =>
      buildPairs(
        investigators,
        { ta, indication, phase, line, regions },
        siteTrialRows,
      ),
    [investigators, ta, indication, phase, line, regions, siteTrialRows],
  );

  const ranked = useMemo(() => {
    let rows = scorePairs(pairs, activeWeights, pillarDefs);
    if (grouped) rows = bestPairPerSite(rows);
    return rows.slice(0, n);
  }, [pairs, activeWeights, pillarDefs, grouped, n]);

  const existingKeys = useMemo(
    () => new Set((plan?.sites ?? []).map((s) => s.site_key)),
    [plan?.sites],
  );

  const activeFilterCount = [
    ta,
    indication,
    phase,
    line,
    regions.length > 0 ? "regions" : "",
  ].filter(Boolean).length;

  const columnTitles: Record<string, string> = useMemo(
    () => ({
      investigator: "Investigator · Site",
      location: "Location",
      experience: "Experience",
      site_score: "Site score",
      investigator_score: "Investigator score",
      score: "Score",
      ...Object.fromEntries(
        pillarDefs.map((p) => [`pillar_${p.key}`, p.label]),
      ),
    }),
    [pillarDefs],
  );

  // Column state merged with the active pillar set: unknown pillar columns
  // drop out, new served pillars append hidden before Score.
  const effectiveColumns = useMemo(() => {
    const known = new Set(Object.keys(columnTitles));
    const kept = columns.filter((c) => known.has(c.fieldName));
    const have = new Set(kept.map((c) => c.fieldName));
    const appended = pillarDefs
      .filter((p) => !have.has(`pillar_${p.key}`))
      .map((p) => ({
        fieldName: `pillar_${p.key}`,
        visibility: ColumnVisibility.HIDDEN,
      }));
    const scoreIdx = kept.findIndex((c) => c.fieldName === "score");
    const cut = scoreIdx < 0 ? kept.length : scoreIdx;
    return [...kept.slice(0, cut), ...appended, ...kept.slice(cut)];
  }, [columns, columnTitles, pillarDefs]);

  // CSV export mirrors the table: structural columns + whatever score
  // columns are currently visible via the column picker.
  const csvExport = useMemo(() => {
    const visible = effectiveColumns.filter(
      (c) => c.visibility === ColumnVisibility.VISIBLE,
    );
    const fields = [
      "Rank",
      "Investigator",
      "Site",
      "City",
      "Country",
      ...visible
        .filter((c) => !["investigator", "location"].includes(c.fieldName))
        .map((c) => columnTitles[c.fieldName] ?? c.fieldName),
    ];
    const rows = ranked.map((r) => [
      r.rank,
      r.inv.name,
      r.site_name,
      r.city,
      r.country,
      ...visible
        .filter((c) => !["investigator", "location"].includes(c.fieldName))
        .map((c) => {
          if (c.fieldName === "experience")
            return `${r.n_profile_trials} of ${r.n_total_trials}`;
          if (c.fieldName === "score") return r.score.toFixed(1);
          if (c.fieldName === "site_score")
            return groupScores(r, activeWeights, pillarDefs).site.toFixed(0);
          if (c.fieldName === "investigator_score")
            return groupScores(
              r,
              activeWeights,
              pillarDefs,
            ).investigator.toFixed(0);
          const pillar = c.fieldName.replace(/^pillar_/, "");
          return r.pillars[pillar] != null
            ? Math.round(r.pillars[pillar] * 100)
            : "";
        }),
    ]);
    return { fields, rows };
  }, [ranked, effectiveColumns, columnTitles, activeWeights, pillarDefs]);

  const sitePct = useMemo(() => {
    const w = normalizeWeights(activeWeights, pillarDefs);
    return Math.round(
      pillarDefs
        .filter((p) => p.group === "site")
        .reduce((sum, p) => sum + w[p.key], 0) * 100,
    );
  }, [activeWeights, pillarDefs]);

  if (!plan) return null;

  const toPlannedSite = (pair: ScoredPair, source: string): PlannedSite => {
    const site = plannedSiteFromInvestigator(pair.inv);
    // Provenance travels with the candidate; enrollment params prefill from
    // the pair's own history so the forecast is evidence-based immediately.
    site.criteria = {
      shortlist_score: pair.score.toFixed(1),
      shortlist_rank: String(pair.rank),
      shortlist_experience: `${pair.n_profile_trials} of ${pair.n_total_trials} trials in profile`,
      shortlist_source: source,
    };
    if (pair.evidence.patients_per_month != null) {
      site.screenedPerMonth = Math.max(
        1,
        Math.round(pair.evidence.patients_per_month * 1.25),
      );
    }
    return site;
  };

  const applyToPlan = (rows: ScoredPair[], source: string): number => {
    const fresh = rows.filter((r) => !existingKeys.has(r.inv_key));
    if (fresh.length) {
      updatePlan(plan.id, {
        sites: [...plan.sites, ...fresh.map((r) => toPlannedSite(r, source))],
      });
    }
    return fresh.length;
  };

  const handleAddAll = () => {
    const added = applyToPlan(ranked, "Shortlist");
    message.success(
      added
        ? `${added} site${added === 1 ? "" : "s"} added — refine on Investigators`
        : "Everything listed is already on the plan",
    );
  };

  const cellRenderers: Record<string, (r: ScoredPair) => React.ReactNode> = {
    investigator: (r) => (
      <div>
        <button
          type="button"
          onClick={() => setActiveInv(r.inv)}
          className="font-medium text-[13px] text-[#1F2933] hover:text-[#0C98D0] text-left transition-colors leading-snug"
        >
          {r.inv.name}
        </button>
        <div className="text-[11.5px] text-[#6B7280]">
          {r.site_name || "No site on record"}
        </div>
      </div>
    ),
    location: (r) => (
      <span className="text-[12px] text-[#6B7280]">
        {[r.city, r.country].filter(Boolean).join(", ")}
      </span>
    ),
    experience: (r) => (
      <span className="whitespace-nowrap">
        <span className="text-[13px] font-bold tabular-nums">
          {r.n_profile_trials}
        </span>{" "}
        <span className="text-[11px] text-[#8A97A3]">
          in profile · {r.n_total_trials} total
        </span>
      </span>
    ),
    site_score: (r) => (
      <span className="text-[12px] text-[#6B7280] tabular-nums">
        {groupScores(r, activeWeights, pillarDefs).site.toFixed(0)}
      </span>
    ),
    investigator_score: (r) => (
      <span className="text-[12px] text-[#6B7280] tabular-nums">
        {groupScores(r, activeWeights, pillarDefs).investigator.toFixed(0)}
      </span>
    ),
    ...Object.fromEntries(
      pillarDefs.map((p) => [
        `pillar_${p.key}`,
        (r: ScoredPair) => (
          <span className="text-[12px] text-[#6B7280] tabular-nums">
            {Math.round((r.pillars[p.key] ?? 0.5) * 100)}
          </span>
        ),
      ]),
    ),
    score: (r) => (
      <Popover
        content={
          <ScoreBreakdown pair={r} weights={activeWeights} defs={pillarDefs} />
        }
        placement="left"
      >
        <span className="text-[14px] font-bold tabular-nums cursor-default">
          {r.score.toFixed(1)}
        </span>
      </Popover>
    ),
  };

  const antColumns: AntColumnsType<ScoredPair> = [
    {
      title: "#",
      key: "rank",
      width: 46,
      fixed: "left",
      render: (_, r) => (
        <span className="text-[12px] font-semibold text-[#8A97A3] tabular-nums">
          {r.rank}
        </span>
      ),
    },
    ...effectiveColumns
      .filter((c) => c.visibility === ColumnVisibility.VISIBLE)
      .map((c) => ({
        title:
          c.fieldName === "score" ? (
            <Tooltip title="Hover any score for its weighted pillar breakdown">
              <span className="cursor-help">
                {columnTitles[c.fieldName] ?? c.fieldName}
              </span>
            </Tooltip>
          ) : (
            (columnTitles[c.fieldName] ?? c.fieldName)
          ),
        key: c.fieldName,
        width: c.fieldName === "investigator" ? 230 : 130,
        render: (_: unknown, r: ScoredPair) =>
          cellRenderers[c.fieldName]?.(r) ?? null,
      })),
    {
      key: "actions",
      fixed: "right",
      width: 96,
      align: "right",
      render: (_, r) =>
        existingKeys.has(r.inv_key) ? (
          <Button size="small" disabled>
            In plan
          </Button>
        ) : (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<PlusOutlined />}
            onClick={() => {
              updatePlan(plan.id, {
                sites: [...plan.sites, toPlannedSite(r, "Shortlist")],
              });
              message.success(`${r.inv.name} added — refine on Investigators`);
            }}
          >
            Add
          </Button>
        ),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2 shrink-0 flex-wrap">
        <h2 className="text-[15px] font-semibold text-[#1F2933] m-0">
          Find Best Sites
        </h2>
        <span className="text-[12px] text-[#6B7280]">
          Recommended (site, investigator) pairs for this plan
        </span>
      </div>

      {/* Toolbar — weights left; filter, download, columns right-justified
          to the table edge (columns outermost), all equal height. */}
      <div className="flex gap-2 mb-3 shrink-0 items-center">
        <IconButton
          icon={<SettingOutlined />}
          title={`Scoring weights — ${preset || "Custom"}`}
          onClick={() => setWeightsOpen(true)}
          style={{ height: "35px", width: "35px" }}
          data-testid="shortlist-weights-button"
        />
        <span className="text-[12px] text-[#6B7280]">
          Weights: <b>{preset || "Custom"}</b>
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <Popover
            trigger="click"
            placement="bottomRight"
            content={
              <div className="w-[280px] flex flex-col gap-2.5 py-1">
                <FilterField
                  label="Therapeutic area"
                  tooltip="Ranks each pair's history by how much of it is in this TA"
                >
                  <Select
                    size="small"
                    className="w-full"
                    placeholder="Any TA"
                    allowClear
                    value={ta || undefined}
                    options={allTas.map((t) => ({ value: t, label: t }))}
                    onChange={(v) => {
                      setTaOverride(v ?? "");
                      setIndicationOverride("");
                    }}
                  />
                </FilterField>
                {ta && (
                  <FilterField
                    label="Indication"
                    tooltip="Optional: narrow the TA to a specific indication"
                  >
                    <Select
                      size="small"
                      className="w-full"
                      placeholder="Any indication"
                      allowClear
                      showSearch
                      value={indication || undefined}
                      options={indicationOptions.map((i) => ({
                        value: i,
                        label: i,
                      }))}
                      onChange={(v) => setIndicationOverride(v ?? "")}
                    />
                  </FilterField>
                )}
                <FilterField
                  label="Phase"
                  tooltip="Experience and fit are weighted toward trials of this phase"
                >
                  <Select
                    size="small"
                    className="w-full"
                    placeholder="Any phase"
                    allowClear
                    value={phase || undefined}
                    options={ALL_PHASES.map((p) => ({ value: p, label: p }))}
                    onChange={(v) => setPhaseOverride(v ?? "")}
                  />
                </FilterField>
                <FilterField
                  label="Line of therapy"
                  tooltip="Optional: match history to the treatment line"
                >
                  <Select
                    size="small"
                    className="w-full"
                    value={line}
                    options={LINE_OPTIONS}
                    onChange={setLine}
                  />
                </FilterField>
                <FilterField
                  label="Region"
                  tooltip="Regions to recommend from; empty means global"
                >
                  <Select
                    size="small"
                    mode="multiple"
                    className="w-full"
                    placeholder="Global"
                    allowClear
                    maxTagCount={1}
                    value={regions}
                    options={allRegions.map((r) => ({ value: r, label: r }))}
                    onChange={setRegions}
                  />
                </FilterField>
                <FilterField
                  label="List size"
                  tooltip="How many pairs to recommend"
                >
                  <Select
                    size="small"
                    className="w-full"
                    value={n}
                    options={SIZE_OPTIONS.map((s) => ({
                      value: s,
                      label: `Top ${s}`,
                    }))}
                    onChange={setN}
                  />
                </FilterField>
                {!simplified && (
                  <FilterField
                    label="View"
                    tooltip="One row per investigator, or collapse to each site's best investigator"
                  >
                    <Select
                      size="small"
                      className="w-full"
                      value={grouped ? "grouped" : "flat"}
                      options={[
                        { value: "flat", label: "Group by Investigator" },
                        { value: "grouped", label: "Group by Site" },
                      ]}
                      onChange={(v) => setGrouped(v === "grouped")}
                    />
                  </FilterField>
                )}
              </div>
            }
          >
            <Badge count={activeFilterCount} size="small" color="#0C98D0">
              <IconButton
                icon={<FilterIcon />}
                title="Filter the recommendation profile"
                style={{ height: "35px", width: "35px" }}
                data-testid="shortlist-filter-button"
              />
            </Badge>
          </Popover>
          <ClientDownloadButton
            fileName={`find-best-sites-${indication || ta || "all"}`}
            fields={csvExport.fields}
            rows={csvExport.rows}
          />
          <ColumnButton
            columns={effectiveColumns}
            setColumns={setColumns}
            lockedColumns={lockedColumns}
            setLockedColumns={setLockedColumns}
            columnsTitleCreator={(field) => columnTitles[field] ?? field}
            initialColumns={DEFAULT_COLUMNS}
            initialLockedColumns={1}
          />
        </div>
      </div>

      {/* Results — the list is the star */}
      <div className="flex-1 min-h-0 rounded-[8px] border border-[#F0F2F4] bg-white overflow-hidden flex flex-col">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#F0F2F4] text-[11.5px] text-[#6B7280] shrink-0">
          <span>
            Top {ranked.length} of {pairs.length} scored
            {grouped ? " sites" : " pairs"}
            {excluded > 0 ? " · " : ""}
            {excluded > 0 && (
              <Tooltip
                title={
                  "Not enough performance history to score — a rank would be guesswork."
                }
              >
                <span className="cursor-help underline decoration-dotted">
                  {excluded} not scored
                </span>
              </Tooltip>
            )}
          </span>
          <span className="flex-1" />
          <Button
            type="link"
            size="small"
            className="px-0 text-[11.5px]"
            icon={<InfoCircleOutlined />}
            onClick={() => setMethodOpen(true)}
          >
            How scoring works
          </Button>
          <Button size="small" onClick={handleAddAll}>
            Add all
          </Button>
        </div>
        {isLoading || metricsLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Spin />
          </div>
        ) : ranked.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <Empty description="No rankable pairs for this profile" />
          </div>
        ) : (
          <Table<ScoredPair>
            dataSource={ranked}
            columns={antColumns}
            rowKey="inv_key"
            size="small"
            scroll={{ x: "max-content", y: TABLE_Y }}
            pagination={{ pageSize: 25, size: "small" }}
          />
        )}
      </div>

      {/* Weights drawer — configuration off-screen until summoned */}
      <Drawer
        title={
          <div className="pr-2">
            <p className="text-[16px] font-bold text-white leading-snug m-0">
              Weights
            </p>
            <p className="text-[13px] font-normal text-white/60 mt-0.5 m-0">
              Defaults derived from cross-trial reliability analysis
            </p>
          </div>
        }
        placement="right"
        width={420}
        open={weightsOpen}
        onClose={() => setWeightsOpen(false)}
        mask={false}
        footer={
          <p className="text-[11.5px] text-[#8A97A3] m-0 leading-relaxed">
            The list re-ranks live as you drag. The site ↔ investigator balance
            is simply the relative weight of the two groups.
          </p>
        }
      >
        <div className="flex flex-wrap gap-1.5 mb-4">
          {Object.keys(WEIGHT_PRESETS).map((name) => (
            <Button
              key={name}
              size="small"
              type={preset === name ? "primary" : "default"}
              className={preset === name ? "bg-[#0C98D0] border-none" : ""}
              onClick={() => {
                setPreset(name);
                setWeights({ ...WEIGHT_PRESETS[name] });
              }}
            >
              {name}
            </Button>
          ))}
        </div>
        {(["site", "investigator"] as const).map((group) => (
          <div key={group} className="mb-4">
            <div
              className={`flex justify-between text-[10.5px] font-bold uppercase tracking-wide pb-1 mb-2 border-b border-[#F0F2F4] ${group === "site" ? "text-[#0A7FB0]" : "text-[#6D5FC8]"}`}
            >
              <span>
                {group === "site" ? "Site pillars" : "Investigator pillars"}
              </span>
              <span className="tabular-nums">
                {group === "site" ? sitePct : 100 - sitePct}%
              </span>
            </div>
            {pillarDefs
              .filter((p) => p.group === group)
              .map((p) => (
                <div key={p.key} className="mb-1.5">
                  <div className="flex justify-between text-[12.5px] text-[#374151]">
                    <span className="inline-flex items-center gap-1.5">
                      {p.label}
                      <Tooltip title={p.description}>
                        <InfoCircleOutlined className="text-[11px] text-[#9CA3AF]" />
                      </Tooltip>
                    </span>
                    <span className="text-[#8A97A3] tabular-nums">
                      {activeWeights[p.key] ?? 0}%
                    </span>
                  </div>
                  <Slider
                    min={0}
                    max={50}
                    value={activeWeights[p.key] ?? 0}
                    onChange={(v: number) => {
                      setPreset("");
                      setWeights({ ...activeWeights, [p.key]: v });
                    }}
                    tooltip={{ open: false }}
                    className="my-1"
                  />
                </div>
              ))}
          </div>
        ))}
      </Drawer>

      <MethodModal open={methodOpen} onClose={() => setMethodOpen(false)} />

      <InvestigatorProfileDrawer
        profile={activeInv}
        onClose={() => setActiveInv(null)}
      />
    </div>
  );
}
